// src/pages/LoginPage.js

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';

// Für alle /auth/*-Requests: vorbereitete Instanz nutzen
// (setzt withCredentials, XSRF-Cookie/Header, baseURL usw.)
import { authApi } from '../axiosInstance';

function LoginPage({ csrfToken, setUser, loginSucceededRef }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Debug: zeige die Basis-URL der Auth-API in der Konsole
  useEffect(() => {
    try {
      // z.B. "https://localhost:3000"
      // hilft sofort zu sehen, ob die .env korrekt geladen wurde
      // eslint-disable-next-line no-console
      console.log('AUTH_API =', authApi?.defaults?.baseURL);
    } catch {
      /* noop */
    }
  }, []);

  // Beim Mount einen "Priming"-Call ausführen, damit der Server
  // den CSRF-Secret-Cookie setzt und wir einen initialen Token-Cookie bekommen.
  useEffect(() => {
    authApi.get('/csrf-token', { withCredentials: true }).catch(() => {
      // nicht schlimm – beim eigentlichen Login greifen wir nochmal zu
    });
  }, []);

  // Fallback: Cookie-Reader (falls wir einmal manuell nachschauen wollen)
  function readCookie(name) {
    const hit = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));
    return hit ? decodeURIComponent(hit.split('=')[1]) : '';
  }

  async function handleLogin(e) {
    e.preventDefault();
    if (submitting) return;

    setErrorMessage('');
    setSubmitting(true);

    try {
      // E-Mail nur für den Request normalisieren (Trim + Lowercase)
      const emailNorm = (email || '').trim().toLowerCase();

      // 1) CSRF-Token holen …
      let token = '';
      try {
        const { data } = await authApi.get('/csrf-token', { withCredentials: true });
        token = data?.csrfToken || '';
      } catch {
        /* … */
      }

      // 2) Login call
      const loginRes = await authApi.post(
        '/login',
        { email: emailNorm, password },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': token, 'X-Requested-With': 'XMLHttpRequest' },
        }
      );

      // 3) Session-/Rolleninfos abholen
      const { data: who } = await authApi.get('/whoami', {
        withCredentials: true,
      });

      // ✅ WICHTIG: userObj aus whoami.data.user ziehen (Variante X)
      let userObj = who?.user || null;

      // Fallback (falls whoami aus irgendeinem Grund keine user-Infos liefert)
      if (!userObj) {
        userObj = loginRes?.data?.user ?? loginRes?.data ?? null;
      }

      const role = who?.role ?? who?.user?.role ?? userObj?.role ?? null;

      // firstLogin heuristisch
      const firstLogin =
        (userObj &&
          (userObj.is_first_login ?? userObj.first_login ?? userObj.firstLogin)) ??
        false;

      // Ref setzen BEVOR setUser, damit der Boot-Effekt kein null mehr schreibt
      if (loginSucceededRef) loginSucceededRef.current = true;

      // Optional im App-State merken
      if (typeof setUser === 'function') {
        setUser(userObj || { email, role });
      }

      // --- robust: Onboarding nicht nur über is_first_login entscheiden ---
      const fn =
        (userObj?.first_name ??
          userObj?.firstName ??
          userObj?.firstname ??
          userObj?.firstname) ??
        '';
      const ln =
        (userObj?.last_name ??
          userObj?.lastName ??
          userObj?.lastname ??
          userObj?.lastname) ??
        '';

      const profileMissing = !String(fn).trim() || !String(ln).trim();

      // ✅ NEU: onboarding_complete berücksichtigen
      const onboardingComplete = Boolean(
        userObj?.onboarding_complete ?? userObj?.onboardingComplete
      );

      const needsOnboarding = Boolean(!onboardingComplete || firstLogin || profileMissing);

      // 4) Weiterleitung anhand Rolle
      if (role === 'mentee') {
        if (needsOnboarding) {
          // ✅ NEU: Skip Passwort immer dann, wenn Passwort schon existiert (also nicht First-Login)
          try {
            if (!firstLogin) sessionStorage.setItem('ONBOARDING_SKIP_PASSWORD', '1');
            else sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
          } catch {}
          navigate('/onboarding-mentee');
        } else {
          // ✅ NEU: Flag sauber entfernen
          try {
            sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD');
          } catch {}
          navigate('/mentee-dashboard');
        }
      } else if (role === 'mentor') {
        needsOnboarding
          ? navigate('/mentoring/onboarding-mentor')
          : navigate('/mentor-dashboard');
      } else if (['hr', 'hr_admin', 'hr_user'].includes(role)) {
        firstLogin ? navigate('/onboarding') : navigate('/hr/overview');
      } else if (role === 'mentixx') {
        navigate('/superadmin');
      } else {
        // Fallback (z. B. unbekannte Rolle)
        navigate('/dashboard');
      }
    } catch (err) {
      // Typische Fehler abbilden
      const status = err?.response?.status;
      const raw =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.error || '';

      // Viele CSRF-Fehler kommen als HTML/Plain mit Text "ForbiddenError: invalid csrf token"
      const isCsrf =
        /csrf/i.test(raw) ||
        /EBADCSRFTOKEN/i.test(raw) ||
        /ForbiddenError: invalid csrf token/i.test(raw);

      if (isCsrf || status === 403) {
        setErrorMessage(
          t(
            'csrfFailed',
            'Sicherheitsprüfung fehlgeschlagen. Bitte Seite neu laden und erneut versuchen.'
          )
        );
      } else if (status === 401) {
        setErrorMessage(t('invalidCredentials', 'Falsches Passwort'));
      } else if (status === 429) {
        setErrorMessage(
          t(
            'tooManyAttempts',
            'Zu viele Login-Versuche. Bitte warte einen Moment und versuche es erneut.'
          )
        );
      } else {
        setErrorMessage(raw || t('loginFailed', 'Login fehlgeschlagen'));
      }
      // eslint-disable-next-line no-console
      console.error('Login fehlgeschlagen:', err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      <h2 className="text-3xl md:text-4xl font-bold mb-8">{t('login')}</h2>

      {errorMessage && (
        <div className="bg-red-600 text-white px-4 py-2 rounded-2xl mb-6">
          {errorMessage}
        </div>
      )}

      <form
        onSubmit={handleLogin}
        className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md"
      >
        <div className="mb-6">
          <label className="block mb-2 font-semibold">{t('email')}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none"
            placeholder={t('emailPlaceholder')}
            autoComplete="username"
          />
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">{t('password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full p-3 rounded bg-gray-700 text-white focus:outline-none"
            placeholder={t('passwordPlaceholder')}
            autoComplete="current-password"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className={`w-full ${
            submitting ? 'opacity-70 cursor-not-allowed' : 'bg-mentixxgreen hover:bg-green-400'
          } text-black font-bold py-3 rounded-2xl transition`}
        >
          {submitting ? t('loggingIn', 'Wird angemeldet…') : t('login')}
        </button>
      </form>

      <p className="mt-6 text-gray-400">
        <Link to="/forgot-password" className="hover:underline">
          {t('forgotPassword.link')}
        </Link>
      </p>
    </div>
  );
}

export default LoginPage;
